import type { ReactNode } from 'react';
import { palette } from '../../theme';
import { type DesignerParams } from './params';

// ---------------------------------------------------------------------------
// Inspector — labelled sliders + show-debug toggle + reset.
//
// Plain React state per the in-game-tooling spec. Sectioned so the
// silhouette test can find related knobs together.
// ---------------------------------------------------------------------------

export function Inspector({
  params,
  setParams,
  onResetRig,
  onResetDefaults,
}: {
  params: DesignerParams;
  setParams: (next: DesignerParams) => void;
  /** Send the rig back to the world origin and clear motion state. */
  onResetRig: () => void;
  /** Restore default slider values AND reset the rig. */
  onResetDefaults: () => void;
}) {
  function update<K extends keyof DesignerParams>(
    key: K,
    value: DesignerParams[K],
  ) {
    setParams({ ...params, [key]: value });
  }

  function updateRig(field: keyof DesignerParams['rig'], v: number) {
    update('rig', { ...params.rig, [field]: v });
  }
  function updateMovement(field: keyof DesignerParams['movement'], v: number) {
    update('movement', { ...params.movement, [field]: v });
  }
  function updateFrontStepper(field: keyof DesignerParams['frontStepper'], v: number) {
    update('frontStepper', { ...params.frontStepper, [field]: v });
  }
  function updateBackStepper(field: keyof DesignerParams['backStepper'], v: number) {
    update('backStepper', { ...params.backStepper, [field]: v });
  }
  function updateTail<F extends keyof DesignerParams['tail']>(
    field: F,
    v: DesignerParams['tail'][F],
  ) {
    update('tail', { ...params.tail, [field]: v });
  }
  function updateTailPitch(index: number, value: number) {
    const next = params.tail.pitches.slice();
    next[index] = value;
    update('tail', { ...params.tail, pitches: next });
  }
  /**
   * Coordinated update: changing tailSegments must resize tail.pitches
   * in lockstep so the per-bone slider count and the rest-curve match.
   * With delta semantics, padding new bones with 0 means "extend
   * straight from the existing tip" (no further curl) — the safe
   * default. The user can dial those new joints in afterward.
   */
  function updateTailSegments(rawValue: number) {
    const nextN = Math.max(1, Math.round(rawValue));
    const oldPitches = params.tail.pitches;
    let nextPitches: number[];
    if (nextN <= oldPitches.length) {
      nextPitches = oldPitches.slice(0, nextN);
    } else {
      nextPitches = oldPitches.slice();
      while (nextPitches.length < nextN) nextPitches.push(0);
    }
    setParams({
      ...params,
      rig: { ...params.rig, tailSegments: nextN },
      tail: { ...params.tail, pitches: nextPitches },
    });
  }
  function updateSpine(field: keyof DesignerParams['spine'], v: number) {
    update('spine', { ...params.spine, [field]: v });
  }
  function updateIdle(field: keyof DesignerParams['idle'], v: number) {
    update('idle', { ...params.idle, [field]: v });
  }

  return (
    <aside
      style={{
        background: 'rgba(0,0,0,0.25)',
        borderLeft: `1px solid ${palette.mossTeal}`,
        padding: 12,
        color: palette.warmBeige,
        fontFamily: 'system-ui, sans-serif',
        fontSize: 12,
        // box-sizing keeps padding inside the 320px column width.
        // overflow shorthand: hide horizontal (kills the spurious x-scroll
        // that appears alongside the y-scroll when sliders bleed past the
        // panel), allow vertical scroll for tall content.
        boxSizing: 'border-box',
        minWidth: 0,
        overflow: 'hidden auto',
        display: 'flex',
        flexDirection: 'column',
        gap: 12,
      }}
    >
      <Section title="Movement">
        <Slider
          label="Move speed (m/s)"
          value={params.movement.moveSpeed}
          min={0}
          max={2.0}
          step={0.05}
          onChange={v => updateMovement('moveSpeed', v)}
        />
        <Slider
          label="Turn speed (rad/s)"
          value={params.movement.turnSpeed}
          min={0}
          max={4.0}
          step={0.1}
          onChange={v => updateMovement('turnSpeed', v)}
        />
      </Section>

      <Section title="Body">
        <Slider
          label="Spine length (m)"
          value={params.rig.spineLength}
          min={0.04}
          max={0.20}
          step={0.005}
          onChange={v => updateRig('spineLength', v)}
        />
        <Slider
          label="Chest length (m)"
          value={params.rig.chestLength}
          min={0.04}
          max={0.20}
          step={0.005}
          onChange={v => updateRig('chestLength', v)}
        />
        <Slider
          label="Neck length (m)"
          value={params.rig.neckLength}
          min={0.02}
          max={0.12}
          step={0.005}
          onChange={v => updateRig('neckLength', v)}
        />
        <Slider
          label="Head length (m)"
          value={params.rig.headLength}
          min={0.02}
          max={0.10}
          step={0.005}
          onChange={v => updateRig('headLength', v)}
        />
        <Slider
          label="Neck lift (m)"
          value={params.rig.neckLift}
          min={0.0}
          max={0.08}
          step={0.005}
          onChange={v => updateRig('neckLift', v)}
        />
        <Slider
          label="Head lift (m)"
          value={params.rig.headLift}
          min={0.0}
          max={0.06}
          step={0.005}
          onChange={v => updateRig('headLift', v)}
        />
      </Section>

      <Section title="Front legs">
        <Slider
          label="Upper (m)"
          value={params.rig.frontLegUpperLength}
          min={0.03}
          max={0.16}
          step={0.005}
          onChange={v => updateRig('frontLegUpperLength', v)}
        />
        <Slider
          label="Lower (m)"
          value={params.rig.frontLegLowerLength}
          min={0.03}
          max={0.16}
          step={0.005}
          onChange={v => updateRig('frontLegLowerLength', v)}
        />
        <Slider
          label="Half-width (m)"
          value={params.rig.frontLegHalfWidth}
          min={0.02}
          max={0.10}
          step={0.005}
          onChange={v => updateRig('frontLegHalfWidth', v)}
        />
      </Section>

      <Section title="Back legs">
        <Slider
          label="Upper (m)"
          value={params.rig.backLegUpperLength}
          min={0.03}
          max={0.18}
          step={0.005}
          onChange={v => updateRig('backLegUpperLength', v)}
        />
        <Slider
          label="Lower (m)"
          value={params.rig.backLegLowerLength}
          min={0.03}
          max={0.18}
          step={0.005}
          onChange={v => updateRig('backLegLowerLength', v)}
        />
        <Slider
          label="Half-width (m)"
          value={params.rig.backLegHalfWidth}
          min={0.02}
          max={0.10}
          step={0.005}
          onChange={v => updateRig('backLegHalfWidth', v)}
        />
      </Section>

      <Section title="Posture">
        <Slider
          label="Crouch factor"
          value={params.rig.crouchFactor}
          min={0.4}
          max={1.0}
          step={0.02}
          onChange={v => updateRig('crouchFactor', v)}
        />
        <Slider
          label="Hip pitch (rad)"
          value={params.rig.hipPitch}
          min={-0.8}
          max={0.4}
          step={0.02}
          onChange={v => updateRig('hipPitch', v)}
        />
        <Slider
          label="Spine pitch (rad)"
          value={params.rig.spineRestPitch}
          min={-0.4}
          max={1.2}
          step={0.02}
          onChange={v => updateRig('spineRestPitch', v)}
        />
        <Slider
          label="Chest pitch (rad)"
          value={params.rig.chestRestPitch}
          min={-0.6}
          max={0.6}
          step={0.02}
          onChange={v => updateRig('chestRestPitch', v)}
        />
        <Slider
          label="Neck pitch (rad)"
          value={params.rig.neckRestPitch}
          min={-0.6}
          max={1.4}
          step={0.02}
          onChange={v => updateRig('neckRestPitch', v)}
        />
        <Slider
          label="Head pitch (rad)"
          value={params.rig.headRestPitch}
          min={-0.8}
          max={0.8}
          step={0.02}
          onChange={v => updateRig('headRestPitch', v)}
        />
        <Slider
          label="Front foot fwd (m)"
          value={params.rig.frontFootForward}
          min={-0.04}
          max={0.08}
          step={0.005}
          onChange={v => updateRig('frontFootForward', v)}
        />
        <Slider
          label="Back foot fwd (m)"
          value={params.rig.backFootForward}
          min={-0.04}
          max={0.10}
          step={0.005}
          onChange={v => updateRig('backFootForward', v)}
        />
      </Section>

      <Section title="Tail / ears">
        <Slider
          label="Tail segments"
          value={params.rig.tailSegments}
          min={3}
          max={16}
          step={1}
          onChange={updateTailSegments}
        />
        <Slider
          label="Tail seg length (m)"
          value={params.rig.tailSegmentLength}
          min={0.03}
          max={0.10}
          step={0.005}
          onChange={v => updateRig('tailSegmentLength', v)}
        />
        <Slider
          label="Ear half-width (m)"
          value={params.rig.earHalfWidth}
          min={0.005}
          max={0.05}
          step={0.002}
          onChange={v => updateRig('earHalfWidth', v)}
        />
        <Slider
          label="Ear seg length (m)"
          value={params.rig.earSegmentLength}
          min={0.01}
          max={0.06}
          step={0.002}
          onChange={v => updateRig('earSegmentLength', v)}
        />
      </Section>

      <Section title="Front stepping">
        <Slider
          label="Threshold (m)"
          value={params.frontStepper.stepThreshold}
          min={0.01}
          max={0.12}
          step={0.005}
          onChange={v => updateFrontStepper('stepThreshold', v)}
        />
        <Slider
          label="Duration (s)"
          value={params.frontStepper.stepDuration}
          min={0.05}
          max={0.50}
          step={0.01}
          onChange={v => updateFrontStepper('stepDuration', v)}
        />
        <Slider
          label="Lookahead (s)"
          value={params.frontStepper.lookahead}
          min={0.0}
          max={0.40}
          step={0.01}
          onChange={v => updateFrontStepper('lookahead', v)}
        />
        <Slider
          label="Arc height (m)"
          value={params.frontStepper.arcHeight}
          min={0.0}
          max={0.10}
          step={0.005}
          onChange={v => updateFrontStepper('arcHeight', v)}
        />
      </Section>

      <Section title="Back stepping">
        <Slider
          label="Threshold (m)"
          value={params.backStepper.stepThreshold}
          min={0.01}
          max={0.16}
          step={0.005}
          onChange={v => updateBackStepper('stepThreshold', v)}
        />
        <Slider
          label="Duration (s)"
          value={params.backStepper.stepDuration}
          min={0.05}
          max={0.50}
          step={0.01}
          onChange={v => updateBackStepper('stepDuration', v)}
        />
        <Slider
          label="Lookahead (s)"
          value={params.backStepper.lookahead}
          min={0.0}
          max={0.50}
          step={0.01}
          onChange={v => updateBackStepper('lookahead', v)}
        />
        <Slider
          label="Arc height (m)"
          value={params.backStepper.arcHeight}
          min={0.0}
          max={0.12}
          step={0.005}
          onChange={v => updateBackStepper('arcHeight', v)}
        />
      </Section>

      <Section title="Tail shape (joint bends, rad)">
        {Array.from({ length: params.rig.tailSegments }, (_, i) => {
          // Delta semantics: each slider is the bend at joint i relative
          // to the previous bone's direction. Missing entries pad to 0
          // (straight extension) so the displayed value matches what the
          // rest-curve generator sees mid-resize.
          const arr = params.tail.pitches;
          const value = i < arr.length ? arr[i] : 0;
          return (
            <Slider
              key={i}
              label={`Joint ${i + 1} bend`}
              value={value}
              min={-2.0}
              max={2.0}
              step={0.02}
              onChange={v => updateTailPitch(i, v)}
            />
          );
        })}
        <Slider
          label="Carry weight"
          value={params.tail.restWeight}
          min={0}
          max={0.4}
          step={0.005}
          onChange={v => updateTail('restWeight', v)}
        />
      </Section>

      <Section title="Tail dynamics">
        <Slider
          label="Base damping"
          value={params.tail.baseDamping}
          min={0}
          max={0.8}
          step={0.01}
          onChange={v => updateTail('baseDamping', v)}
        />
        <Slider
          label="Tip damping"
          value={params.tail.tipDamping}
          min={0}
          max={0.8}
          step={0.01}
          onChange={v => updateTail('tipDamping', v)}
        />
        <Slider
          label="Turn response"
          value={params.tail.turnResponse}
          min={0}
          max={0.2}
          step={0.005}
          onChange={v => updateTail('turnResponse', v)}
        />
        <Slider
          label="Iterations"
          value={params.tail.iterations}
          min={1}
          max={10}
          step={1}
          onChange={v => updateTail('iterations', Math.round(v))}
        />
      </Section>

      <Section title="Tail idle">
        <Slider
          label="Sway amp (m)"
          value={params.tail.idleAmp}
          min={0}
          max={0.06}
          step={0.002}
          onChange={v => updateTail('idleAmp', v)}
        />
        <Slider
          label="Sway freq (Hz)"
          value={params.tail.idleFreq}
          min={0}
          max={2}
          step={0.05}
          onChange={v => updateTail('idleFreq', v)}
        />
        <Slider
          label="Wave cycles"
          value={params.tail.idleWaveCycles}
          min={0}
          max={2}
          step={0.05}
          onChange={v => updateTail('idleWaveCycles', v)}
        />
      </Section>

      <Section title="Spine flex">
        <Slider
          label="Bend per rate"
          value={params.spine.bendPerRate}
          min={0}
          max={1.0}
          step={0.02}
          onChange={v => updateSpine('bendPerRate', v)}
        />
        <Slider
          label="Smoothing"
          value={params.spine.smoothing}
          min={1}
          max={20}
          step={0.5}
          onChange={v => updateSpine('smoothing', v)}
        />
      </Section>

      <Section title="Idle noise">
        <Slider
          label="Head yaw"
          value={params.idle.headYawAmp}
          min={0}
          max={0.5}
          step={0.01}
          onChange={v => updateIdle('headYawAmp', v)}
        />
        <Slider
          label="Head pitch"
          value={params.idle.headPitchAmp}
          min={0}
          max={0.3}
          step={0.01}
          onChange={v => updateIdle('headPitchAmp', v)}
        />
        <Slider
          label="Ear twitch"
          value={params.idle.earAmp}
          min={0}
          max={1.0}
          step={0.02}
          onChange={v => updateIdle('earAmp', v)}
        />
        <Slider
          label="Ear period (s)"
          value={params.idle.earPeriod}
          min={0.2}
          max={3.0}
          step={0.05}
          onChange={v => updateIdle('earPeriod', v)}
        />
        <Slider
          label="Breath rate (Hz)"
          value={params.idle.breathRate}
          min={0.2}
          max={2.0}
          step={0.05}
          onChange={v => updateIdle('breathRate', v)}
        />
        <Slider
          label="Breath amplitude"
          value={params.idle.breathAmp}
          min={0}
          max={0.03}
          step={0.001}
          onChange={v => updateIdle('breathAmp', v)}
        />
      </Section>

      <Section title="Debug">
        <Toggle
          label="Show debug overlays"
          value={params.showDebug}
          onChange={v => update('showDebug', v)}
        />
      </Section>

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: '1fr 1fr',
          gap: 8,
          marginTop: 4,
        }}
      >
        <button type="button" onClick={onResetRig} style={resetButtonStyle}>
          Reset rig
        </button>
        <button
          type="button"
          onClick={onResetDefaults}
          style={resetButtonStyle}
        >
          Reset to defaults
        </button>
      </div>
    </aside>
  );
}

const resetButtonStyle: React.CSSProperties = {
  background: 'transparent',
  border: `1px solid ${palette.mossTeal}`,
  color: palette.warmBeige,
  padding: '6px 10px',
  borderRadius: 4,
  cursor: 'pointer',
  fontSize: 12,
  minWidth: 0,
};

function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div>
      <div
        style={{
          fontSize: 11,
          letterSpacing: 1.2,
          textTransform: 'uppercase',
          color: palette.goldenSun,
          marginBottom: 6,
          opacity: 0.85,
        }}
      >
        {title}
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
        {children}
      </div>
    </div>
  );
}

function Slider({
  label,
  value,
  min,
  max,
  step,
  onChange,
}: {
  label: string;
  value: number;
  min: number;
  max: number;
  step: number;
  onChange: (v: number) => void;
}) {
  return (
    <label
      style={{
        display: 'grid',
        // minmax(0, 1fr) lets the range input shrink below its
        // default min-content size so the row never forces horizontal
        // overflow on the parent panel.
        gridTemplateColumns: '110px minmax(0, 1fr) 50px',
        gap: 6,
        alignItems: 'center',
        minWidth: 0,
      }}
    >
      <span
        style={{
          fontSize: 11,
          opacity: 0.85,
          minWidth: 0,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}
      >
        {label}
      </span>
      <input
        type="range"
        min={min}
        max={max}
        step={step}
        value={value}
        onChange={e => onChange(parseFloat(e.target.value))}
        style={{ accentColor: palette.acornOrange, minWidth: 0, width: '100%' }}
      />
      <span style={{ fontSize: 11, opacity: 0.7, textAlign: 'right' }}>
        {value.toFixed(3)}
      </span>
    </label>
  );
}

function Toggle({
  label,
  value,
  onChange,
}: {
  label: string;
  value: boolean;
  onChange: (v: boolean) => void;
}) {
  return (
    <label style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
      <input
        type="checkbox"
        checked={value}
        onChange={e => onChange(e.target.checked)}
      />
      <span style={{ fontSize: 12 }}>{label}</span>
    </label>
  );
}
